import React, { useState, useEffect, useRef } from "react";
import { Popover, Checkbox, Spin, Flex, Typography } from "antd";
import { TagOutlined } from "@ant-design/icons";
import orderTagService from "../../services/orderTagService";

const { Text } = Typography;

const AssignTagsPopover = ({ taskKey, allTags, onTagsChanged }) => {
  const [assignedUids, setAssignedUids] = useState([]);
  const [localAllTags, setLocalAllTags] = useState(allTags || []);
  const [localIsTagged, setLocalIsTagged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);

  // Load the real assigned state from API on mount — never derive from parent's old-tags field
  useEffect(() => {
    orderTagService.getTagsForTask(taskKey).then((res) => {
      if (res?.success) {
        const uids = (res.data || []).map((t) => t.uid);
        setAssignedUids(uids);
        setLocalIsTagged(uids.length > 0);
      }
    });
  }, [taskKey]);

  const flyTagToSidebar = (tag) => {
    const srcEl = triggerRef.current;
    if (!srcEl) return;
    const srcRect = srcEl.getBoundingClientRect();

    const targetEl = document.querySelector(`[data-tag-uid="${tag.uid}"]`);
    const targetRect = targetEl?.getBoundingClientRect();
    const destX = targetRect ? targetRect.left + 8 : 24;
    const destY = targetRect
      ? targetRect.top + targetRect.height / 2 - 10
      : window.innerHeight * 0.6;

    const flyEl = document.createElement("div");
    flyEl.textContent = tag.name;
    Object.assign(flyEl.style, {
      position: "fixed",
      left: `${srcRect.left}px`,
      top: `${srcRect.top}px`,
      background: `${tag.color}28`,
      color: tag.color,
      border: `1px solid ${tag.color}`,
      padding: "2px 10px",
      borderRadius: "12px",
      fontSize: "12px",
      fontWeight: "600",
      zIndex: "99999",
      pointerEvents: "none",
      whiteSpace: "nowrap",
      boxShadow: `0 2px 12px ${tag.color}55`,
      opacity: "1",
      transform: "scale(1.15)",
      transition: "none",
    });
    document.body.appendChild(flyEl);

    // Double rAF ensures the browser paints the start position before animating
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        Object.assign(flyEl.style, {
          transition:
            "left 0.55s cubic-bezier(0.4,0,0.2,1), top 0.55s cubic-bezier(0.4,0,0.2,1), transform 0.55s, opacity 0.4s 0.3s",
          left: `${destX}px`,
          top: `${destY}px`,
          transform: "scale(0.75)",
          opacity: "0",
        });
      });
    });

    setTimeout(() => {
      flyEl.remove();
      window.dispatchEvent(new CustomEvent("tag-highlight", { detail: { uid: tag.uid } }));
    }, 600);
  };

  const loadAssigned = async () => {
    setLoading(true);
    try {
      const [assignedRes, allTagsRes] = await Promise.all([
        orderTagService.getTagsForTask(taskKey),
        orderTagService.getTags(),
      ]);
      if (assignedRes?.success) {
        const uids = (assignedRes.data || []).map((t) => t.uid);
        setAssignedUids(uids);
        setLocalIsTagged(uids.length > 0);
      }
      if (allTagsRes?.success) {
        setLocalAllTags(allTagsRes.data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (visible) => {
    setOpen(visible);
    if (visible) loadAssigned();
  };

  const handleToggle = async (uid, checked) => {
    if (checked) {
      await orderTagService.assignTag(uid, taskKey);
      setAssignedUids((prev) => {
        const next = [...prev, uid];
        setLocalIsTagged(next.length > 0);
        return next;
      });
      const tag = localAllTags.find((t) => t.uid === uid);
      if (tag) flyTagToSidebar(tag);
    } else {
      await orderTagService.removeTag(uid, taskKey);
      setAssignedUids((prev) => {
        const next = prev.filter((id) => id !== uid);
        setLocalIsTagged(next.length > 0);
        return next;
      });
    }
    window.dispatchEvent(new Event("tag-assignment-changed"));
    if (onTagsChanged) onTagsChanged();
  };

  const content = (
    <div style={{ minWidth: 200, maxHeight: 300, overflowY: "auto" }}>
      {loading ? (
        <Flex justify="center" style={{ padding: 12 }}>
          <Spin size="small" />
        </Flex>
      ) : localAllTags.length === 0 ? (
        <Text type="secondary" style={{ padding: 8, display: "block" }}>
          No tags yet. Create tags from the sidebar.
        </Text>
      ) : (
        localAllTags.map((tag) => (
          <Flex key={tag.uid} align="center" gap={8} style={{ padding: "4px 0" }}>
            <Checkbox
              checked={assignedUids.includes(tag.uid)}
              onChange={(e) => handleToggle(tag.uid, e.target.checked)}
            />
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: tag.color,
                display: "inline-block",
                flexShrink: 0,
              }}
            />
            <Text style={{ fontSize: 13, maxWidth: 220 }} ellipsis title={tag.name}>{tag.name}</Text>
          </Flex>
        ))
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      title="Assign Tags"
      trigger="click"
      open={open}
      onOpenChange={handleOpenChange}
      placement="bottomLeft"
    >
      <span ref={triggerRef} style={{ display: "inline-flex" }}>
        <TagOutlined
          style={{
            cursor: "pointer",
            fontSize: 16,
            color: localIsTagged ? "#22c55e" : "#9CA3AF",
            background: localIsTagged ? "#f0fdf4" : "transparent",
            borderRadius: "50%",
            padding: 3,
            border: localIsTagged ? "1px solid #86efac" : "1px solid transparent",
            transition: "all 0.2s",
          }}
          title="Assign tags"
        />
      </span>
    </Popover>
  );
};

export default AssignTagsPopover;
